import { FluentButton } from "../../../../components/FluentUI/Fluent-Button";
import FluentUiIcon from "../../../../assets/fluent-ui-icon";
import { Toast, ToastTitle, ToastBody, Tooltip } from "@fluentui/react-components";
import WidgetGridFilter from "./widget-grid-filter";
import dayjs from "../../../../util/dayjs-config";

const LAST_UPDATED_COLORS = {
  recent: "#121212",
  medium: "#F29A1D",
  old: "#C11919",
};

// .NET's DateTime.MinValue serializes as "0001-01-01..." which V8's Date parser
// misinterprets as 1901 (two-digit year inference). Moment's custom parser
// returned year 1 here; dayjs delegates to native Date, so detect the sentinel
// by string prefix instead of `.year() === 1`.
const isSentinelMinDate = (value) =>
  typeof value === "string" && value.startsWith("0001-");

export const getLastUpdatedColor = (lastUpdated) => {
  if (isSentinelMinDate(lastUpdated)) {
    return LAST_UPDATED_COLORS.recent;
  }
  const m = dayjs(lastUpdated);
  if (!m.isValid()) {
    return LAST_UPDATED_COLORS.recent;
  }

  const daysAgo = dayjs().diff(m, "day");
  if (daysAgo <= 3) return LAST_UPDATED_COLORS.recent;
  if (daysAgo <= 7) return LAST_UPDATED_COLORS.medium;
  return LAST_UPDATED_COLORS.old;
};

export const getLastUpdateTooltip = (lastUpdated) => {
  if (!lastUpdated || isSentinelMinDate(lastUpdated)) return null;
  const m = dayjs.utc(lastUpdated);
  if (!m.isValid()) {
    return null;
  }
  return `Last updated on ${m.local().format("MMMM D, YYYY [at] h:mm A")}`;
};

export const normalizeDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime()) || d.getUTCFullYear() === 1) {
    return null;
  }
  return d;
};

export const formatLastUpdatedDate = (input) => {
  if (!input || isSentinelMinDate(input)) {
    return "–";
  }
  const m = dayjs(input);
  if (!m.isValid()) {
    return "–";
  }
  return new Date(input).toLocaleDateString();
};

export const submitButton = (isSubmitDisabled, handleSubmit) => {
  return (
    <FluentButton
      id="tb-submit-btn"
      text="Submit"
      appearance="primary"
      icon={
        <FluentUiIcon
          name="DocumentArrowUpRegular"
          size={20}
          color={isSubmitDisabled ? "#A0A0A0" : "#FFFFFF"}
          disabled={isSubmitDisabled}
        />
      }
      onClick={handleSubmit}
      disabled={isSubmitDisabled}
    />
  )
}

export const renderLastUpdatedTooltipHeader = (label = "Last Updated") => (
  <Tooltip
    content={
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div>
          <span style={{ color: LAST_UPDATED_COLORS.recent, fontWeight: "bold" }}>■</span> Updated within the last three days
        </div>
        <div>
          <span style={{ color: LAST_UPDATED_COLORS.medium, fontWeight: "bold" }}>■</span> Updated more than three days ago, but less than a week ago
        </div>
        <div>
          <span style={{ color: LAST_UPDATED_COLORS.old, fontWeight: "bold" }}>■</span> Updated more than a week ago
        </div>
      </div>
    }
    relationship="label"
  >
    <span>{label}</span>
  </Tooltip>
);

export const buildToast = (dispatchToast) => (title, message, intent = "success") => {
  dispatchToast(
    <Toast>
      <ToastTitle>{title}</ToastTitle>
      <ToastBody>{message}</ToastBody>
    </Toast>,
    { intent }
  );
};

export const renderTooltipHeader = (label) => (
  <Tooltip content={label} relationship="label">
    <span>{label}</span>
  </Tooltip>
);

export const renderlastUpdatedTooltipHeader = (label) => (
  <span className="d-flex ">
    <Tooltip content={label} relationship="label">
      <span>{label}</span>
    </Tooltip>
    <Tooltip content={ <>
          <div className='consumerRetailBlack tooltipCRbox'></div><div>Updated within the last week</div>
          <div className='consumerRetailYellow tooltipCRbox'></div><div>Updated more than a week ago, but less than two weeks ago</div>
          <div className='consumerRetailRed tooltipCRbox'></div><div>Updated more than two weeks ago</div>
        </>} relationship="label">
      <span className="ml-1 d-flex align-items-center" >
        <FluentUiIcon
          name="InfoRegular"
          size={16}
          color="#000"
        />
      </span>
    </Tooltip>

  </span>
);

export const renderLabelWithInfoTooltipHeader = (label, infotext) => (
  <span className="d-flex ">
    <Tooltip content={label} relationship="label">
      <span>{label}</span>
    </Tooltip>
    <Tooltip content={<span>{infotext}</span>} relationship="label">
      <span className="ml-1 d-flex align-items-center" >
        <FluentUiIcon
          name="InfoRegular"
          size={16}
          color="#000"
        />
      </span>
    </Tooltip>

  </span>
);

export const renderLabelWithInfoTooltipHeaderWithContent = (label, content) => (
  <span className="d-flex ">
    <Tooltip content={label} relationship="label">
      <span>{label}</span>
    </Tooltip>
    <Tooltip content={content} relationship="label">
      <span className="ml-1 d-flex align-items-center" >
        <FluentUiIcon
          name="InfoRegular"
          size={16}
          color="#000"
        />
      </span>
    </Tooltip>

  </span>
);

export const createColumnMenu = (processedData, searchValue = "") => {
  return function ColumnMenuWrapper(props) {
    const isSearchActive = searchValue.trim().length > 0;
    return (
      <WidgetGridFilter
        props={props}
        gridData={processedData}
        disabled={isSearchActive}
      />
    );
  };
};

export const highlightMatch = (text, search) => {
  if (!search || !text) return text;

  const trimmedSearch = search.trim();
  if (!trimmedSearch) return text;

  const lowerText = text.toLowerCase();
  const lowerSearch = trimmedSearch.toLowerCase();
  const index = lowerText.indexOf(lowerSearch);

  if (index === -1) return text;

  const before = text.slice(0, index);
  const match = text.slice(index, index + trimmedSearch.length);
  const after = text.slice(index + trimmedSearch.length);

  return (
    <>
      {before}
      <strong>{match}</strong>
      {after}
    </>
  );
};

export const removeDuplicateHeaderRows = (sheet) => {
  const rows = sheet.rows || [];

  // Remove any header rows that have no cells or all empty cells
  sheet.rows = rows.filter((row) => {
    if (row.type === "header") {
      const hasContent = row.cells && row.cells.some((cell) => cell.value !== undefined && cell.value !== null && cell.value !== "");
      return hasContent;
    }
    return true;
  });

  // After filtering, ensure rows are not hidden and freeze row is correct
  sheet.rows.forEach((row) => {
    row.hidden = false;
  });

  // Reset freeze pane to just 1 header row
  const headerCount = sheet.rows.filter((r) => r.type === "header").length;
  if (headerCount > 0) {
    sheet.frozenRows = headerCount;
  }
};